import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase/config';
import { createNotification } from '../services/NotificationSystem';
import { Scale, CheckCircle2, AlertTriangle, XCircle, X, FileText, FileSpreadsheet, Landmark } from 'lucide-react';

const BANKS = ['국민', '신한', '우리', '하나', '농협', '기업', 'SC제일', '씨티', '대구', '부산', '광주', '경남', '전북', '제주', '우체국', '새마을금고', '신협', '수협', '케이뱅크', '카카오뱅크', '토스뱅크'];

/**
 * 정산시스템 (/clinic/settlement, 치과 전용)
 * 기공소에서 받은 세금계산서를 거래명세서와 월별로 대사(확인)하는 화면
 *  - 어느 달에 세금계산서를 못 받았는지 (미수취)
 *  - 받은 세금계산서 금액이 거래명세서와 맞는지 (금액 불일치)
 * 행 = 거래 기공소, 열 = 최근 6개월, 셀 클릭 → 명세서·계산서 상세 비교
 */
const MONTHS_TO_SHOW = 6;

const CELL_STATUS = {
    match: { text: '일치', color: '#059669', bg: '#ECFDF5', Icon: CheckCircle2 },
    mismatch: { text: '금액 불일치', color: '#D97706', bg: '#FFFBEB', Icon: AlertTriangle },
    missing: { text: '미수취', color: '#DC2626', bg: '#FEF2F2', Icon: XCircle },
    invoiceOnly: { text: '명세서 없음', color: '#6B7280', bg: '#F3F4F6', Icon: AlertTriangle },
    none: { text: '-', color: '#CBD5E1', bg: 'transparent', Icon: null },
};

const fmt = (n) => (n || 0).toLocaleString('ko-KR');

const toDate = (v) => {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    if (v instanceof Date) return v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};
const monthKey = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null;

function ClinicSettlement({ user }) {
    const uid = auth.currentUser?.uid;
    const myIds = [...new Set([uid, user?.companyId].filter(Boolean))];

    const [matrix, setMatrix] = useState({});     // labId → { labName, months: { 'YYYY-MM': { stmts:[], invs:[] } } }
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);   // { labName, month, stmts, invs }

    // CMS 자동출금
    const [cmsAccount, setCmsAccount] = useState(null);
    const [showCmsModal, setShowCmsModal] = useState(false);
    const [cmsForm, setCmsForm] = useState({ bankName: '', accountNumber: '', accountHolder: '', consentAgreed: false });
    const [cmsSaving, setCmsSaving] = useState(false);

    // 치과 직원(companyBusinessType) 포함 (2026-08-05 서류 개방)
    const businessType = user?.businessType || user?.companyBusinessType || user?.userType;
    const isDental = businessType === 'dental' || businessType === 'clinic';

    // 최근 N개월 키 목록 (최신 → 과거)
    const months = (() => {
        const list = [];
        const now = new Date();
        for (let i = 0; i < MONTHS_TO_SHOW; i++) {
            list.push(monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
        }
        return list;
    })();

    const fetchAll = useCallback(async () => {
        if (myIds.length === 0) return;
        setLoading(true);
        try {
            const map = {};
            const ensure = (labId, labName) => {
                if (!map[labId]) map[labId] = { labName: labName || '', months: {} };
                if (labName && !map[labId].labName) map[labId].labName = labName;
                return map[labId];
            };
            const ensureCell = (labId, mk) => {
                const row = ensure(labId);
                if (!row.months[mk]) row.months[mk] = { stmts: [], invs: [] };
                return row.months[mk];
            };

            // ① 내가 받은 확정 거래명세서 (기공소 → 우리 치과)
            for (const id of myIds) {
                const snap = await getDocs(query(
                    collection(db, 'transactionStatements'),
                    where('toUserId', '==', id),
                    where('status', '==', 'confirmed'),
                ));
                snap.forEach(d => {
                    const s = { id: d.id, ...d.data() };
                    // 발행처가 삭제·정정한 명세서는 합산 제외 (2026-10-23 검토 회의 — 정정본과 이중 합산 방지)
                    if (s.deletedBySender) return;
                    const mk = monthKey(toDate(s.endDate) || toDate(s.publishedAt) || toDate(s.createdAt));
                    if (!mk || !months.includes(mk) || !s.fromUserId) return;
                    ensureCell(s.fromUserId, mk).stmts.push(s);
                    ensure(s.fromUserId, s.fromUserName);
                });
            }

            // ② 내가 받은 세금계산서 (buyerInfo.userId = 우리 치과)
            for (const id of myIds) {
                const snap = await getDocs(query(
                    collection(db, 'taxInvoices'),
                    where('buyerInfo.userId', '==', id),
                ));
                snap.forEach(d => {
                    const inv = { id: d.id, ...d.data() };
                    if (inv.type === 'commission') return;   // 플랫폼 수수료 계산서 제외
                    const mk = monthKey(toDate(inv.issueDate) || toDate(inv.createdAt));
                    const labId = inv.createdBy || inv.companyId;
                    if (!mk || !months.includes(mk) || !labId) return;
                    ensureCell(labId, mk).invs.push(inv);
                    ensure(labId, inv.supplierInfo?.businessName || inv.supplierInfo?.name);
                });
            }

            // 기공소명 보강
            await Promise.all(Object.keys(map).map(async labId => {
                if (map[labId].labName) return;
                try {
                    const u = await getDoc(doc(db, 'users', labId));
                    map[labId].labName = u.exists() ? (u.data().businessName || u.data().name || labId) : labId;
                } catch (_) { map[labId].labName = labId; }
            }));

            setMatrix(map);
        } catch (e) {
            console.error('정산 대사 조회 실패:', e);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, user?.companyId]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // CMS 계좌 조회
    const fetchCms = useCallback(async () => {
        const clinicId = user?.companyId || uid;
        if (!clinicId) return;
        try {
            const snap = await getDoc(doc(db, 'cmsAccounts', clinicId));
            if (snap.exists() && snap.data().status === 'active') setCmsAccount(snap.data());
            else setCmsAccount(null);
        } catch (_) { }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, user?.companyId]);
    useEffect(() => { fetchCms(); }, [fetchCms]);

    // 기공소가 보낸 CMS 수금 요청 (대기 중)
    const [cmsRequests, setCmsRequests] = useState([]);
    const fetchCmsRequests = useCallback(async () => {
        const clinicId = user?.companyId || uid;
        if (!clinicId) return;
        try {
            const snap = await getDocs(query(
                collection(db, 'cmsRequests'),
                where('clinicId', '==', clinicId),
                where('status', '==', 'pending')
            ));
            setCmsRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (_) { setCmsRequests([]); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, user?.companyId]);
    useEffect(() => { fetchCmsRequests(); }, [fetchCmsRequests]);

    const registerCms = async () => {
        if (!cmsForm.bankName || !cmsForm.accountNumber || !cmsForm.accountHolder) {
            alert('은행, 계좌번호, 예금주를 입력해주세요.'); return;
        }
        if (!cmsForm.consentAgreed) {
            alert('자동출금 이체 동의에 체크해주세요.'); return;
        }
        setCmsSaving(true);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'registerCmsAccount');
            await fn(cmsForm);
            // 대기 중인 기공소 수금 요청을 동의 완료로 전환 + 기공소에 알림
            for (const req of cmsRequests) {
                try {
                    await updateDoc(doc(db, 'cmsRequests', req.id), { status: 'accepted', respondedAt: new Date() });
                    await createNotification({
                        recipientId: req.labId,
                        type: 'cms_accepted',
                        title: 'CMS 자동출금 동의 완료',
                        message: `${req.clinicName || '치과'}에서 자동출금 계좌를 사전 신청했습니다. CMS사 연동 완료 후 매월 ${req.withdrawalDay || 5}일 자동 수금이 시작됩니다.`,
                        link: '/transactions',
                    });
                } catch (e) { console.warn('CMS 요청 상태 갱신 실패:', e); }
            }
            alert('자동출금 사전 신청이 완료되었습니다.\nCMS사 연동이 완료되면 안내 드린 뒤, 매월 정해진 날짜에 확정 거래명세서 금액이 자동 출금·정산됩니다.\n(연동 완료 전까지는 실제 출금이 발생하지 않습니다)');
            setShowCmsModal(false);
            await fetchCms();
            await fetchCmsRequests();
        } catch (e) {
            alert(e.message || '등록에 실패했습니다.');
        } finally {
            setCmsSaving(false);
        }
    };

    const cancelCms = async () => {
        if (!window.confirm('자동출금을 해지하시겠습니까?')) return;
        setCmsSaving(true);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'cancelCmsAccount');
            await fn();
            await fetchCms();
        } catch (e) {
            alert(e.message || '해지에 실패했습니다.');
        } finally {
            setCmsSaving(false);
        }
    };

    if (!isDental) {
        return <div style={{ maxWidth: 640, margin: '60px auto', textAlign: 'center', color: '#6B7280' }}>정산시스템은 치과 회원 전용입니다.</div>;
    }

    // 셀 상태 판정: 명세서 합계 vs 세금계산서 공급가 합계
    const cellStatus = (cell) => {
        if (!cell) return { key: 'none', stmtTotal: 0, invTotal: 0 };
        const stmtTotal = cell.stmts.reduce((s, x) => s + (x.totalAmount || 0), 0);
        const invTotal = cell.invs.reduce((s, x) => s + (x.supplyAmount || 0), 0);
        let key;
        if (stmtTotal === 0 && invTotal === 0) key = 'none';
        else if (stmtTotal > 0 && invTotal === 0) key = 'missing';
        else if (stmtTotal === 0 && invTotal > 0) key = 'invoiceOnly';
        else key = stmtTotal === invTotal ? 'match' : 'mismatch';
        return { key, stmtTotal, invTotal };
    };

    const labIds = Object.keys(matrix).sort((a, b) => (matrix[a].labName || '').localeCompare(matrix[b].labName || ''));

    // 상단 요약 (전월 기준: 미수취/불일치 수)
    const prevMonth = months[1];
    let missingCount = 0, mismatchCount = 0;
    labIds.forEach(labId => {
        const { key } = cellStatus(matrix[labId].months[prevMonth]);
        if (key === 'missing') missingCount++;
        if (key === 'mismatch') mismatchCount++;
    });

    const monthLabel = (mk) => `${Number(mk.split('-')[1])}월`;

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <Scale size={25} color="#2563EB" /> 정산시스템
            </h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 16, lineHeight: 1.7 }}>
                기공소에서 받은 <b>세금계산서</b>를 <b>거래명세서(의뢰 내역)</b>와 월별로 자동 대사합니다.
                어느 달에 계산서를 못 받았는지, 금액이 명세서와 맞는지 한눈에 확인하세요. 셀을 누르면 상세 비교가 열립니다.
            </p>

            {/* 기공소의 CMS 수금 요청 배너 */}
            {!cmsAccount && cmsRequests.length > 0 && (
                <div style={{ border: '1px solid #BFDBFE', background: '#EFF6FF', borderRadius: 12, padding: '12px 16px', marginBottom: 12, fontSize: 13.5, color: '#1E40AF' }}>
                    <b>자동출금 동의 요청 {cmsRequests.length}건</b>
                    {cmsRequests.map(r => (
                        <div key={r.id} style={{ marginTop: 4 }}>
                            · <b>{r.labName || '기공소'}</b>에서 매월 <b>{r.withdrawalDay}일</b> 기공료 자동출금 동의를 요청했습니다.
                        </div>
                    ))}
                    <div style={{ marginTop: 6, fontSize: 12.5, color: '#3B82F6' }}>
                        아래 [자동출금 등록]으로 계좌를 등록하면 동의가 완료됩니다.
                    </div>
                </div>
            )}

            {/* CMS 자동출금 카드 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', border: '1px solid #E5E7EB', borderRadius: 14, padding: '14px 18px', marginBottom: 16, background: cmsAccount ? '#F0FDF4' : '#fff' }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: cmsAccount ? '#059669' : '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Landmark size={20} color="#fff" />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a' }}>
                        기공료 자동출금 (CMS) {cmsAccount && <span style={{ color: '#059669' }}>· 등록됨</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2, lineHeight: 1.6 }}>
                        {cmsAccount
                            ? <>{cmsAccount.bankName} {cmsAccount.accountMasked} ({cmsAccount.accountHolder}) — 매월 5일 전월 확정 명세서 금액 자동 정산 {cmsAccount.mode === 'test' && <b style={{ color: '#D97706' }}>· 테스트 모드 (CMS사 연동 대기)</b>}</>
                            : '계좌를 등록하면 CMS사 연동 완료 후 매월 5일 전월 확정 거래명세서 금액이 자동 출금되어 기공소에 정산됩니다. 지금 등록하면 사전 신청됩니다.'}
                    </div>
                </div>
                {cmsAccount ? (
                    <button onClick={cancelCms} disabled={cmsSaving} style={{ background: '#fff', color: '#DC2626', border: '1px solid #FECACA', padding: '9px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>해지</button>
                ) : (
                    <button onClick={() => setShowCmsModal(true)} style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>자동출금 등록</button>
                )}
            </div>

            {/* 카드(PG) 결제 안내 — 미수금 분쟁 예방 */}
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: '11px 16px', marginBottom: 16, fontSize: 12.5, color: '#6B7280', background: '#F9FAFB', lineHeight: 1.7 }}>
                💳 <b style={{ color: '#374151' }}>카드(PG) 결제 안내</b> — 기공료를 카드로 결제하면 결제 시점에 대금 지급이 확정되어,
                미수금으로 인한 분쟁이나 법적 절차(내용증명·지급명령 등)로 이어질 부담을 크게 줄일 수 있습니다.
                CMS 자동출금 또는 카드 결제를 이용하면 기공소·치과 모두 정산 관리가 간편해집니다.
            </div>

            {(missingCount > 0 || mismatchCount > 0) && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    {missingCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FEF2F2', color: '#DC2626', padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                            <XCircle size={16} /> 지난달 세금계산서 미수취 {missingCount}곳
                        </div>
                    )}
                    {mismatchCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFFBEB', color: '#D97706', padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                            <AlertTriangle size={16} /> 지난달 금액 불일치 {mismatchCount}곳
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>대사 중...</div>
            ) : labIds.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>
                    최근 {MONTHS_TO_SHOW}개월 내 확정 거래명세서·세금계산서 내역이 없습니다.
                </div>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, background: '#fff' }}>
                        <thead>
                            <tr style={{ background: '#F9FAFB' }}>
                                <th style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: 700, whiteSpace: 'nowrap' }}>기공소</th>
                                {months.map(mk => (
                                    <th key={mk} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontWeight: 700, whiteSpace: 'nowrap' }}>{monthLabel(mk)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {labIds.map(labId => (
                                <tr key={labId}>
                                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: 600, whiteSpace: 'nowrap' }}>{matrix[labId].labName}</td>
                                    {months.map(mk => {
                                        const cell = matrix[labId].months[mk];
                                        const { key, stmtTotal, invTotal } = cellStatus(cell);
                                        const s = CELL_STATUS[key];
                                        const Icon = s.Icon;
                                        return (
                                            <td key={mk} style={{ padding: 6, borderBottom: '1px solid #F3F4F6', textAlign: 'center' }}>
                                                {key === 'none' ? (
                                                    <span style={{ color: s.color }}>-</span>
                                                ) : (
                                                    <button
                                                        onClick={() => setDetail({ labName: matrix[labId].labName, month: mk, stmts: cell.stmts, invs: cell.invs, stmtTotal, invTotal })}
                                                        style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2, background: s.bg, color: s.color, border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', minWidth: 78 }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{Icon && <Icon size={12} />} {s.text}</span>
                                                        <span style={{ fontWeight: 500, fontSize: 11 }}>{fmt(stmtTotal || invTotal)}원</span>
                                                    </button>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5, color: '#6B7280' }}>
                <span><CheckCircle2 size={12} color="#059669" /> 일치: 명세서와 계산서 금액 동일</span>
                <span><XCircle size={12} color="#DC2626" /> 미수취: 명세서는 있는데 세금계산서를 못 받음</span>
                <span><AlertTriangle size={12} color="#D97706" /> 불일치: 금액이 서로 다름</span>
            </div>

            {/* CMS 등록 모달 */}
            {showCmsModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>기공료 자동출금 등록</h3>
                            <button onClick={() => setShowCmsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 12px', lineHeight: 1.6 }}>
                            매월 5일, 전월 확정 거래명세서 금액이 아래 계좌에서 자동 출금되어 기공소에 정산됩니다.
                        </p>
                        {/* 테스트 모드 사전 고지 — 실출금 전 안내 */}
                        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '11px 14px', marginBottom: 16, fontSize: 13, color: '#92400E', lineHeight: 1.7 }}>
                            <b>현재 자동출금 시스템 연동 준비 중입니다.</b><br />
                            지금 등록하시면 사전 신청되며, 연동 완료 후 안내 드린 뒤 출금이 시작됩니다.
                            그 전까지는 실제 출금이 발생하지 않습니다.
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' }}>은행</label>
                                <select value={cmsForm.bankName} onChange={e => setCmsForm(p => ({ ...p, bankName: e.target.value }))}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}>
                                    <option value="">선택하세요</option>
                                    {BANKS.map(b => <option key={b} value={b}>{b}은행</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' }}>계좌번호</label>
                                <input value={cmsForm.accountNumber} onChange={e => setCmsForm(p => ({ ...p, accountNumber: e.target.value }))}
                                    placeholder="- 없이 숫자만" style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' }}>예금주</label>
                                <input value={cmsForm.accountHolder} onChange={e => setCmsForm(p => ({ ...p, accountHolder: e.target.value }))}
                                    placeholder="예금주명" style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 }}>
                                <input type="checkbox" checked={cmsForm.consentAgreed} onChange={e => setCmsForm(p => ({ ...p, consentAgreed: e.target.checked }))}
                                    style={{ width: 16, height: 16, marginTop: 2, accentColor: '#2563EB' }} />
                                <span>위 계좌에서 확정 거래명세서 금액의 <b>자동출금 이체(CMS)</b>에 동의합니다. 출금 전 금액은 정산시스템에서 확인할 수 있으며 언제든 해지할 수 있습니다.</span>
                            </label>
                            <button onClick={registerCms} disabled={cmsSaving}
                                style={{ background: '#2563EB', color: '#fff', border: 'none', padding: '13px', borderRadius: 8, fontWeight: 700, fontSize: 14.5, cursor: 'pointer' }}>
                                {cmsSaving ? '신청 중...' : '자동출금 사전 신청'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 상세 비교 모달 */}
            {detail && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 560, width: '100%', maxHeight: '86vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{detail.labName} · {monthLabel(detail.month)} 정산 비교</h3>
                            <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
                        </div>
                        <div style={{ fontSize: 14, margin: '8px 0 16px', padding: '10px 14px', borderRadius: 10, background: detail.stmtTotal === detail.invTotal ? '#ECFDF5' : '#FEF2F2', color: detail.stmtTotal === detail.invTotal ? '#059669' : '#DC2626', fontWeight: 700 }}>
                            명세서 합계 {fmt(detail.stmtTotal)}원 vs 세금계산서 공급가 {fmt(detail.invTotal)}원
                            {detail.stmtTotal !== detail.invTotal && ` (차액 ${fmt(Math.abs(detail.stmtTotal - detail.invTotal))}원)`}
                        </div>

                        <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                            <FileText size={15} color="#2563EB" /> 거래명세서 ({detail.stmts.length}건)
                        </div>
                        {detail.stmts.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#9CA3AF', padding: '6px 0 14px' }}>해당 월 확정 명세서가 없습니다.</div>
                        ) : detail.stmts.map(s => (
                            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '7px 0', borderBottom: '1px solid #F3F4F6' }}>
                                <span>
                                    {toDate(s.startDate)?.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ~ {toDate(s.endDate)?.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                                    <span style={{ color: '#9CA3AF' }}> · {s.totalTeeth || (s.items || []).length}건</span>
                                </span>
                                <b>{fmt(s.totalAmount)}원</b>
                            </div>
                        ))}

                        <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, margin: '16px 0 8px' }}>
                            <FileSpreadsheet size={15} color="#7C3AED" /> 받은 세금계산서 ({detail.invs.length}건)
                        </div>
                        {detail.invs.length === 0 ? (
                            <div style={{ fontSize: 13, color: '#DC2626', padding: '6px 0', fontWeight: 600 }}>
                                이 달 세금계산서를 받지 못했습니다. 기공소에 발행을 요청하세요.
                            </div>
                        ) : detail.invs.map(inv => (
                            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '7px 0', borderBottom: '1px solid #F3F4F6' }}>
                                <span>
                                    {inv.invoiceNumber || inv.id.slice(0, 8)}
                                    {inv.isElectronic && <span style={{ marginLeft: 6, fontSize: 11, color: '#059669', fontWeight: 700 }}>전자</span>}
                                </span>
                                <span>공급가 <b>{fmt(inv.supplyAmount)}원</b> <span style={{ color: '#9CA3AF' }}>+ 세액 {fmt(inv.taxAmount)}원</span></span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClinicSettlement;
